import { bike } from "../../../MotorCalculation/configJson";
import { coverageOf } from "./companyRules";

/**
 * What the premium engine (calcCarAndBike) is fed, built from the form and the
 * vehicle(s) being priced.
 *
 * The engine already takes the OD rate band (`baseRateQuery`) and the TP rate
 * (`tp.TP`) as SEPARATE arguments, so a policy sold by two insurers, each with
 * its own vehicle, is priced in ONE call:
 *   - Total A (own damage) from the OD vehicle: its CC band, the IDV, discount,
 *     nil dep and zone;
 *   - Total B (third party) from the TP vehicle: its CC's TP rate, TPPD, PA.
 * With one vehicle (single company) both come from it, exactly as before.
 *
 * Pure: no state, no network — the same inputs give the same engine call.
 */

/** A vehicle's CC as a number, from "125", "125 cc" or a number; 160 (and `usedFallback`) when it has none. */
export const parseCc = (vehicle) => {
  const raw = vehicle?.CC ?? vehicle?.cc;
  // The master stores cc as a STRING, and the band is chosen by comparing it
  // with numbers: any stray character ("125 cc") makes every comparison NaN —
  // false — and the vehicle falls through to the LAST band (>350cc), the most
  // expensive one. Parsing the digits first makes the band depend on the
  // number, not on the formatting.
  const parsed = parseInt(String(raw ?? "").replace(/[^0-9]/g, ""), 10);
  const ok = Number.isFinite(parsed) && parsed > 0;
  return { cc: ok ? parsed : 160, usedFallback: !ok, raw };
};

/** The OD base-rate column for a CC. */
export const ccBand = (cc) =>
  cc <= 75 && cc >= 0 ? "<75" : cc <= 150 && cc >= 75 ? ">75" : cc <= 350 && cc > 150 ? "<350" : ">350";

/** The third-party rate for a CC, from the TP master (`tpData.twoWheeler.fiveYearTp`). */
export const tpRateFor = (cc, tpData) => {
  const tp = tpData?.twoWheeler?.fiveYearTp;
  return cc <= 75 && cc >= 0
    ? tp?.lessthan75
    : cc <= 150 && cc >= 75
    ? tp?.lessthan150
    : cc <= 350 && cc > 150
    ? tp?.lessthan350
    : tp?.greaterthan350;
};

const zeroDepreciationInput = (formData) => {
  if (!(formData.zeroDepreciation && formData.zeroDepreciationPercentage)) return {};
  // Accept either 0.615 (desired) or 6.15 and normalize to 0.615
  const raw = parseFloat(formData.zeroDepreciationPercentage);
  let normalized = isNaN(raw) ? 0 : raw;
  // If someone passed 6.15 (10x), normalize down to 0.615
  if (normalized > 1 && normalized <= 10) {
    normalized = Number((normalized / 10).toFixed(3));
  }
  return { zeroDepreciation: normalized };
};

const discountInput = (formData) => {
  if (!formData.discount) return {};
  const discountNum = parseFloat(formData.discount);
  if (!isNaN(discountNum) && discountNum > 0 && discountNum <= 100) {
    return { "discountonODPremium(%)": discountNum };
  }
  return {};
};

/**
 * Everything the engine call needs.
 *
 * @param {Object} p
 * @param {Object} p.formData     form values (idv, zone, manufacturingYear, discount, zeroDepreciation..., paCover, paCoverAmount, tppdRestrict)
 * @param {Object} p.odVehicle    the vehicle priced for OWN DAMAGE (cc, label)
 * @param {Object} [p.tpVehicle]  the vehicle priced for THIRD PARTY; the OD vehicle when omitted (single company)
 * @param {Object} p.tpData       the TP rate master
 * @returns {{ inputData, zoneData, baseRateQuery, tpObj, odCc, tpCc, usedFallback }}
 */
export const buildPremiumRun = ({ formData, odVehicle, tpVehicle, tpData }) => {
  // OD only / TP only: the engine's own TP-only branch prices a third-party policy (Total A = 0, Total B = LP + PA +
  // TPPD, the same Total B a full policy has); an OD-only policy is priced as Full with the TP side taken out in
  // finalizePremium. The shared engine itself is untouched.
  const coverage = coverageOf(formData);
  const od = parseCc(odVehicle);
  const tp = tpVehicle ? parseCc(tpVehicle) : od;
  const makeModelValue = odVehicle?.Value || odVehicle?.label || "Two Wheeler";

  const inputData = {
    formId: 2, // New Two Wheeler Vehicle
    policyType: coverage === "tp" ? "TP" : "Full",
    iDV: coverage === "tp" ? 0 : parseFloat(formData.idv) || 0,
    yearOfManufacture: parseInt(formData.manufacturingYear) || new Date().getFullYear(),
    // An echo in the result; the OD side's, since Total A is the headline figure.
    cC: od.cc,
    // Zone drives the base rate: zoneData[zone]["cc"+band].value differs
    // between A and B. It comes from the selected RTO City/Location's zone, or
    // the user's choice when that RTO has none.
    zone: formData.zone === "B" ? "B" : "A",
    makeModel: makeModelValue,
    // PA Cover's amount is charged as the PA-to-Owner-Driver component of the
    // TP premium, so it flows through Total B / Net Premium / GST@18% like any
    // other TP line — same as typing it into that field on the standalone
    // Calculator page.
    // OD only: no PA in the engine (it sits in Total B, which an OD-only policy does not have) — PA is its own amount.
    ...(formData.paCover && coverage !== "od" && {
      pAOwnerDriver: Number(formData.paCoverAmount) || 0,
    }),
    // Own-damage items: not for a third-party-only policy.
    ...(coverage !== "tp" ? zeroDepreciationInput(formData) : {}),
    ...(coverage !== "tp" ? discountInput(formData) : {}),
    // TPPD Restrict as the "yes" string VehiclePremiumCalculator expects (a third-party item: not for OD only)
    ...(formData.tppdRestrict && coverage !== "od" && { tPPDRestrict: "yes" }),
  };

  return {
    inputData,
    zoneData: bike?.lessThenFiveYears, // a new vehicle uses the less-than-five-years rates
    baseRateQuery: ccBand(od.cc),
    tpObj: { TP: tpRateFor(tp.cc, tpData) },
    odCc: od.cc,
    tpCc: tp.cc,
    usedFallback: od.usedFallback || tp.usedFallback,
  };
};

/**
 * Re-compute tax and the final premium from the engine's own Net Premium
 * [A + B] — it already reflects TPPD Restrict (and every other line), so this is
 * the same GST@18% step the standalone Calculator applies — and stamp which CC
 * each side was priced on. Mutates and returns `result`.
 */
export const finalizePremium = (result, { odCc, tpCc, coverage } = {}) => {
  // OD only: the engine priced Full; the third-party side (LP, PA, TPPD ...) is not part of this policy. Taken out
  // here, BEFORE tax, so Net / GST / Final are the own-damage figures alone.
  if (result && coverage === "od") {
    ["totalBodPremium", "LP", "pAOwnerDriver", "pAUnnamedPassenger", "lLtoPaidDriver", "tppd", "llToPassengers", "lPGKit_B"].forEach(
      (key) => {
        if (key in result) result[key] = 0;
      }
    );
    result.netPremiumAB = Number(result.totalAodPremium) || 0;
  }
  if (result && typeof result.netPremiumAB === "number") {
    result.tax = Number(((result.netPremiumAB * 18) / 100).toFixed(2));
    result.finalPremium = Math.round(result.netPremiumAB + result.tax);
  }
  if (result) {
    result.odCc = odCc;
    result.tpCc = tpCc;
  }
  return result;
};
